import { css } from "@emotion/css";
import { ExternalLink, LegacyDataTable, LegacyDataTableBody, LegacyDataTableHeader, LegacyDataTableHeaderColumn, LegacyDataTableRow, LegacyDataTableRowColumn } from "@octopusdeploy/design-system-components";
import { fontWeight } from "@octopusdeploy/design-system-tokens";
import type { ServerTaskApprovalBffResponse } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React from "react";
import Chip from "~/components/Chips/Chip";
import { EnvironmentChip, TeamChip } from "~/components/Chips/index";
import InternalLink from "~/components/Navigation/InternalLink/index";
interface ServerTaskApprovalsTableProps {
    serverTaskApprovals: ServerTaskApprovalBffResponse[];
}
export function ServerTaskApprovalsTable({ serverTaskApprovals }: ServerTaskApprovalsTableProps) {
    return (<LegacyDataTable>
            <LegacyDataTableHeader>
                <LegacyDataTableRow>
                    <LegacyDataTableHeaderColumn aria-label={"Provider"}>Provider</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn aria-label={"Approval State"}>State</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn aria-label={"Change Request"}>Change Request</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn aria-label={"Project"}>Project</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn aria-label={"Environment"}>Environment</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn aria-label={"Environment"}>Description</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn aria-label={"Task State"}>Task State</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn aria-label={"Approvers"}>Approvers</LegacyDataTableHeaderColumn>
                </LegacyDataTableRow>
            </LegacyDataTableHeader>
            <LegacyDataTableBody>
                {serverTaskApprovals.map((approval) => (<LegacyDataTableRow key={approval.Id}>
                        <LegacyDataTableRowColumn>{approval.ApprovalProvider.DisplayName}</LegacyDataTableRowColumn>
                        <LegacyDataTableRowColumn>{getApprovalStateDescription(approval)}</LegacyDataTableRowColumn>
                        <LegacyDataTableRowColumn>{getChangeLink(approval)}</LegacyDataTableRowColumn>
                        <LegacyDataTableRowColumn>
                            <InternalLink to={links.projectDashboardPage.generateUrl({ spaceId: approval.SpaceId, projectSlug: approval.Project.Id })}>
                                <div className={styles.approvalTaskName}>{approval.Project.DisplayName}</div>
                            </InternalLink>
                        </LegacyDataTableRowColumn>
                        <LegacyDataTableRowColumn>
                            <EnvironmentChip environmentName={approval.Environment.DisplayName}/>
                        </LegacyDataTableRowColumn>
                        <LegacyDataTableRowColumn>{approval.Task.DisplayName}</LegacyDataTableRowColumn>
                        <LegacyDataTableRowColumn>{getTaskLink(approval)}</LegacyDataTableRowColumn>
                        <LegacyDataTableRowColumn>
                            {approval.ApprovingUsers.map((user) => (<React.Fragment>
                                    <Chip key={user.Id} description={user.DisplayName}>
                                        {user.DisplayName}{" "}
                                    </Chip>
                                </React.Fragment>))}
                            {approval.ApprovingTeams.map((team) => ({ Id: team.Id, Name: team.DisplayName })).map((team) => (<React.Fragment>
                                    <TeamChip key={team.Id} team={team}/>{" "}
                                </React.Fragment>))}
                        </LegacyDataTableRowColumn>
                    </LegacyDataTableRow>))}
            </LegacyDataTableBody>
        </LegacyDataTable>);
}
function getApprovalStateDescription({ ApprovalState, ApprovalsCount, MinimumApproversRequired }: ServerTaskApprovalBffResponse) {
    if (ApprovalState === "preapproval") {
        let state = "Pending";
        if (MinimumApproversRequired !== undefined && 0 < MinimumApproversRequired) {
            state += ` ${ApprovalsCount ?? 0} / ${MinimumApproversRequired}`;
        }
        return state;
    }
    if (ApprovalState === "approved") {
        return "Approved";
    }
    if (ApprovalState === "postapproval") {
        return "Completed";
    }
    return "Unknown";
}
function getTaskLink(approval: ServerTaskApprovalBffResponse) {
    return (<InternalLink to={links.taskPage.generateUrl({ taskId: approval.Task.Id })}>
            <div className={styles.approvalTaskName}>{approval.ExecutionState}</div>
        </InternalLink>);
}
function getChangeLink(approval: ServerTaskApprovalBffResponse) {
    if (approval.IsExternal) {
        return <ExternalLink href={approval.ExternalLink}>{approval.Number}</ExternalLink>;
    }
    return (<InternalLink to={links.taskPage.generateUrl({ taskId: approval.Task.Id })}>
            <div className={styles.approvalTaskName}>{approval.Number}</div>
        </InternalLink>);
}
const styles = {
    approvalTaskName: css({
        fontWeight: fontWeight["500"],
    }),
};
